import { SOCIAL_OG_IMAGE, SOCIAL_OG_IMAGE_ALT } from "@/lib/social-image";
import { BlogHero } from "@/components/BlogHero";
import { blogHeroUrl, blogHeroAlt } from "@/data/_media/blog-hero";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { SITE } from "@/lib/site-config";
import {
  buildJsonLd,
  jsonLdScript,
  webPageSchema,
  breadcrumbListSchema,
  articleSchema,
  faqPageSchema,
} from "@/lib/schema";
import { canonicalAndHreflang } from "@/lib/hreflang";

/** Japanese mirror of the police clearance certificate guide. */
const TITLE = "タイの無犯罪証明書（Police Clearance）を海外で使う — 完全ガイド";
const DESC =
  "タイの無犯罪証明書の申請方法、必要書類、その後の領事局・大使館認証の手順、そして差し戻しの典型的な理由を整理します。";
const PATH = "/blog/police-clearance-certificate-guide";
const URL = `/ja${PATH}`;
const PUBLISHED = "2026-08-10";

const FAQ = [
  {
    question: "タイの無犯罪証明書はどこが発行しますか？",
    answer:
      "タイ王国警察庁（Royal Thai Police）の犯罪記録部門が発行します。本人申請が原則ですが、規定に従い代理人による申請も可能です。",
  },
  {
    question: "必要な書類は何ですか？",
    answer:
      "パスポートまたは身分証、住居関係の資料、指紋票、申請書、そして使用目的を示す書類（ビザの要求書、雇用主の書面、入学許可など）が一般的です。目的により添付書類は変わります。",
  },
  {
    question: "取得にはどのくらいかかりますか？",
    answer:
      "照会範囲や追加書類の有無により、数営業日から数週間程度です。領事局認証と大使館認証が必要な場合は、その分の日数を別に確保してください。",
  },
  {
    question: "証明書に有効期間はありますか？",
    answer:
      "発行機関が期限を明記しないことが多い一方、提出先は発行から3〜6か月以内を求めることが少なくありません。提出先の書面要件を基準にしてください。",
  },
  {
    question: "翻訳と認証も必要ですか？",
    answer:
      "海外で使用する場合は通常、提出先の言語へ翻訳し、タイ外務省領事局の認証、続いて提出先国の在タイ大使館・領事館の認証を受けます。ハーグ条約加盟国では経路が異なる場合があります。",
  },
  {
    question: "タイ国外からでも申請できますか？",
    answer:
      "可能です。多くの場合は在外のタイ大使館・領事館経由で提出するか、認証済みの委任状により代理人がタイ国内で手続きします。指紋採取の方法は事前に窓口へ確認してください。",
  },
  {
    question: "差し戻しの主な原因は何ですか？",
    answer:
      "氏名スペルがパスポートと不一致、指紋票が不鮮明、使用目的の記載漏れ、発行が古く有効期間切れ、認証の順序違いなどです。",
  },
];

export const Route = createFileRoute("/ja/blog/police-clearance-certificate-guide")({
  component: JaPccGuide,
  loader: () => ({
    h: {
      meta: [
        { title: ctrTitle(`${TITLE} — ${SITE.name}`) },
        { name: "description", content: ctrDesc(DESC) },
        { property: "og:title", content: ctrTitle(TITLE) },
        { property: "og:description", content: ctrDesc(DESC) },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://${SITE.canonicalHost}${URL}` },
        { property: "og:locale", content: "ja" },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        { property: "og:image:alt", content: blogHeroAlt("police-clearance-certificate-guide", "ja") ?? SOCIAL_OG_IMAGE_ALT },
        { name: "twitter:image", content: blogHeroUrl("police-clearance-certificate-guide") ?? SOCIAL_OG_IMAGE },
        {
          name: "keywords",
          content:
            "タイ 無犯罪証明書, police clearance タイ, タイ 警察証明 取得, 無犯罪証明 認証, タイ 犯罪経歴証明書",
        },
      ],
      links: canonicalAndHreflang(PATH, null, "ja"),
      scripts: [
        jsonLdScript(
          buildJsonLd([
            webPageSchema({ url: URL, title: TITLE, description: DESC, lastModified: PUBLISHED, speakable: true }),
            breadcrumbListSchema([
              { name: "ホーム", url: "/ja" },
              { name: "ガイド", url: "/blog" },
              { name: TITLE, url: URL },
            ]),
            articleSchema({
            image: blogHeroUrl("police-clearance-certificate-guide"),
              url: URL,
              headline: TITLE,
              description: DESC,
              datePublished: PUBLISHED,
              dateModified: PUBLISHED,
              articleSection: "Police Clearance",
            }),
            faqPageSchema(FAQ),
          ]),
        ),
      ],
    },
  }),
  head: ({ loaderData }) => loaderData?.h ?? {},
});

const DOCS: string[] = [
  "パスポート原本（海外使用時はパスポートのスペルを基準にする）",
  "タイの身分証または在留を示す書類（該当する場合）",
  "住居に関する資料または住所証明",
  "指紋票（窓口が指定する様式で採取）",
  "使用目的を示す書類：ビザの要求書、雇用主の書面、入学許可など",
  "代理申請の場合：認証済みの委任状と代理人の身分証",
];

const STEPS: string[] = [
  "提出先が求める版・言語・発行時期の要件を先に確認する",
  "身分書類、住所資料、目的を示す書類を揃える",
  "指紋を採取し、申請を提出する（本人または規定に沿った代理人）",
  "証明書を受領し、氏名スペル・生年月日・発行日を照合する",
  "提出先の要件に沿って翻訳し、領事局認証を受ける",
  "提出先国の在タイ大使館・領事館で認証を受けてから提出する",
];

const MISTAKES: string[] = [
  "氏名スペルがパスポートと一致せず、同一人物と確認できない",
  "要件確認より先に翻訳・認証を進め、順序を誤った",
  "指紋票が不鮮明、または様式が要件に合わない",
  "発行が古く、提出時点で有効期間の要件を満たさない",
  "原本や認証済み謄本ではなく単純コピーを使用した",
];

function JaPccGuide() {
  return (
    <article className="mx-auto max-w-3xl px-4 py-12 prose prose-slate dark:prose-invert">
      <nav className="text-sm mb-6 not-prose">
        <Link to="/ja" className="text-muted-foreground hover:text-foreground">
          ホーム
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/blog" className="text-muted-foreground hover:text-foreground">
          ガイド
        </Link>
      </nav>

      <h1>{TITLE}</h1>
      <BlogHero slug="police-clearance-certificate-guide" locale="ja" />
      <p className="answer-summary speakable-summary">
        タイの無犯罪証明書は警察庁が発行し、申請には身分書類・住所資料・指紋票・使用目的を示す書類が必要です。
        海外で使う場合は通常、翻訳、タイ外務省領事局の認証、提出先国の在タイ大使館・領事館の認証が続きます。提出先の期限と言語要件を先に確認し、順序どおり進めれば再取得を避けられます。
      </p>

      <h2>1. 必要な書類</h2>
      <ul>
        {DOCS.map((d) => (
          <li key={d}>{d}</li>
        ))}
      </ul>

      <h2>2. 手続きの順序</h2>
      <ol>
        {STEPS.map((s) => (
          <li key={s}>{s}</li>
        ))}
      </ol>

      <h2>3. よくある差し戻し理由</h2>
      <ul>
        {MISTAKES.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>

      <h2>4. よくある質問</h2>
      <dl>
        {FAQ.map((f) => (
          <div key={f.question}>
            <dt>
              <strong>{f.question}</strong>
            </dt>
            <dd>{f.answer}</dd>
          </div>
        ))}
      </dl>

      <h2>5. 参考リンクと関連サービス</h2>
      <p className="text-sm">
        参考：{" "}
        <a href="https://www.royalthaipolice.go.th/" target="_blank" rel="noreferrer">
          タイ王国警察庁
        </a>{" "}
        ·{" "}
        <a href="https://consular.mfa.go.th/en/publicservice/legalization" target="_blank" rel="noreferrer">
          タイ外務省 領事局
        </a>
      </p>
      <ul className="not-prose mt-4 grid gap-2 text-sm">
        <li>
          <Link to="/ja/pcc" className="text-primary underline">
            無犯罪証明書の取得サポート
          </Link>
        </li>
        <li>
          <Link to="/ja/lgz" className="text-primary underline">
            文書認証・領事認証サービス
          </Link>
        </li>
        <li>
          <Link to="/ja/sworn" className="text-primary underline">
            宣誓・登録翻訳者サービス
          </Link>
        </li>
      </ul>
      <p className="text-sm text-muted-foreground">
        内容確認日 {PUBLISHED}。本ガイドは計画のための一般情報であり、いかなる機関の決定を示すものではありません。手続き前に発行機関および提出先機関で最新要件をご確認ください。
      </p>
    </article>
  );
}
